import { useEffect, useState, type ReactNode } from 'react';
import { CheckCircleFilled, ClockCircleOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Divider, Flex, Skeleton, Spin, Typography, theme } from 'antd';
import { Link, Navigate, useSearchParams } from 'react-router';
import { fontFamilySerif, Logotipo } from '@titularis/ui';
import { api } from '../api';
import { registrarEvento, registrarTela } from '../analytics';
import { useSession } from '../auth';
import { reais } from '../comprar';
import { MiniaturaDoProduto } from '../components/Produto';
import { ehLei, produto as produtoPorChave } from '../produtos';
import { dataPorExtenso } from '../texto';

/**
 * A compra concluída.
 *
 * **Era um `Result` dentro de Configurações > Plano**, e essa era a última coisa
 * que alguém via depois de gastar quinhentos ou seiscentos reais: o cabeçalho
 * "Configurações", as abas "Conta / Desempenho / Plano", o título "Plano" e a
 * linha "Gerencie seu acesso e os limites da conta" vinham antes do aviso de
 * que o pagamento tinha sido confirmado - e em tela de celular era preciso
 * rolar para chegar ao sinal verde. Logo abaixo, o rodapé da mesma tela
 * oferecia o produto recém-comprado.
 *
 * Agora é rota própria e ocupa a tela (D-3). O que ela tem de fazer, nesta
 * ordem: provar que deu certo (o que abriu, por quanto tempo, quanto custou e
 * sob que número), e abrir uma porta - porque ninguém compra para "ter acesso",
 * compra para voltar à questão que errou. **Não há oferta nenhuma aqui.**
 *
 * `/configuracoes/plano/sucesso` continua de pé, redirecionando para cá com a
 * query intacta: a URL de retorno viaja **gravada dentro da cobrança**, e um Pix
 * gerado hoje volta por ela semanas depois (G-RETURN).
 */

/**
 * Quanto tempo esperar o webhook depois de voltar do pagamento.
 *
 * O provedor devolve o comprador **antes** de o webhook chegar: são dois
 * caminhos independentes, e o redirecionamento do navegador costuma ganhar a
 * corrida. Sem isto, quem acabou de pagar veria "você ainda não tem acesso".
 * Vinte segundos cobrem com folga o 1,65 segundo medido em produção, e param
 * antes de virar espera irritante.
 *
 * **É um prazo, e não um contador de tentativas.** O contador vinha do
 * `PlanoPage` e nunca contou o que prometia: ele era incrementado de dentro do
 * `refetchInterval`, que o React Query chama a **cada render** (o
 * `setOptions` recalcula o intervalo, e a opção é um objeto novo toda vez).
 * Cada incremento causava um render, que chamava o intervalo de novo - as dez
 * tentativas se esgotavam em milissegundos, antes da primeira resposta chegar,
 * e o comprador lia "o provedor ainda não confirmou" sem que uma única
 * pergunta a mais tivesse sido feita.
 */
const ESPERA_MS = 20_000;
const INTERVALO_MS = 2_000;

export function CompraSucessoPage() {
  const { token } = theme.useToken();
  const [busca] = useSearchParams();
  const pedidoId = busca.get('pedido');
  const { data: sessao } = useSession();

  const [desistiu, setDesistiu] = useState(false);

  useEffect(() => registrarTela('/compra/sucesso'), []);

  /*
   * O prazo corre no relógio, e não no número de renders. Quando ele vence, o
   * texto muda e as duas consultas param - as duas leem este mesmo estado.
   */
  useEffect(() => {
    const id = setTimeout(() => setDesistiu(true), ESPERA_MS);
    return () => clearTimeout(id);
  }, []);

  const status = useQuery({
    queryKey: ['study-status'],
    queryFn: () => api.studyStatus(),
    refetchInterval: (query) => {
      const temAcesso = (query.state.data?.acessos ?? []).some((acesso) => acesso.ativo);
      return temAcesso || desistiu ? false : INTERVALO_MS;
    },
  });

  /*
   * O recibo. Acompanha a mesma espera do acesso porque nasce do mesmo evento:
   * é o webhook que marca o pedido como pago e cria o direito - e para pelos
   * mesmos dois motivos, para não ficar perguntando pelo pedido a cada dois
   * segundos enquanto a aba estiver aberta.
   */
  const compras = useQuery({
    queryKey: ['minhas-compras'],
    queryFn: () => api.minhasCompras(),
    refetchInterval: (query) => {
      const achou = (query.state.data ?? []).some(
        (item) => item.id === pedidoId && item.status === 'pago',
      );
      return achou || desistiu ? false : INTERVALO_MS;
    },
  });

  const catalogo = useQuery({
    queryKey: ['billing-products'],
    queryFn: () => api.listProducts(),
    staleTime: 5 * 60 * 1000,
  });

  const acessos = status.data?.acessos ?? [];
  const ativos = acessos.filter((acesso) => acesso.ativo);
  const compra = compras.data?.find((item) => item.id === pedidoId);
  const doCatalogo = catalogo.data?.find((item) => item.key === compra?.produto.key);

  /*
   * O que **esta** compra abriu, e não tudo o que a conta tem: quem já era
   * assinante de uma lei avulsa e comprou o Enac tem de ver o que mudou agora.
   * As chaves saem do catálogo, que é quem sabe o que o produto concede.
   */
  const chaves = doCatalogo?.grants ?? [];
  const liberado =
    chaves.length > 0
      ? chaves.every((chave) => ativos.some((acesso) => acesso.product === chave))
      : ativos.length > 0;

  /** Compra que só abre lei comentada: o caminho dela é a Legislação, não o Enac. */
  const soLeis = chaves.length > 0 && chaves.every((chave) => ehLei(chave));

  useEffect(() => {
    if (!liberado || !pedidoId) return;
    /*
     * Uma vez **por pedido**, e não por montagem: recarregar a página ou voltar
     * para ela depois dispararia um segundo `purchase_confirmed` da mesma
     * compra, inflando a conversão. O `sessionStorage` morre com a aba, que é
     * o tempo em que a pessoa pode voltar a esta tela pelo botão de voltar.
     */
    const chave = `titularis:compra-registrada:${pedidoId}`;
    try {
      if (window.sessionStorage.getItem(chave)) return;
      window.sessionStorage.setItem(chave, '1');
    } catch {
      // Navegação anônima com armazenamento bloqueado: registra e segue.
    }
    registrarEvento({ nome: 'purchase_confirmed', pedido: pedidoId });
  }, [liberado, pedidoId]);

  /*
   * Sem pedido no endereço não há compra a confirmar - quem chega assim está
   * procurando o estado da conta, que tem tela própria.
   */
  if (!pedidoId) return <Navigate to="/configuracoes/plano" replace />;

  /*
   * O catálogo entra na espera junto com os outros dois: é dele que saem as
   * chaves desta compra, e sem elas o `liberado` cairia no "tem algum acesso
   * ativo" - o que faria a tela dizer "tudo certo" para quem já tinha uma lei
   * avulsa e ainda espera o webhook do que acabou de comprar.
   */
  const carregando = status.isLoading || compras.isLoading || catalogo.isLoading;

  return (
    <Flex
      vertical
      align="center"
      style={{
        minHeight: '100dvh',
        background: token.colorBgLayout,
        padding: `${token.paddingLG}px ${token.padding}px ${token.paddingXL}px`,
      }}
    >
      <Flex vertical gap={token.marginLG} style={{ width: '100%', maxWidth: 560 }}>
        <Flex justify="center" style={{ paddingBlock: token.padding }}>
          <Logotipo altura={26} />
        </Flex>

        {carregando ? (
          <Card>
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        ) : (
          <>
            <Flex vertical align="center" gap={token.marginXS} style={{ textAlign: 'center' }}>
              {liberado ? (
                <CheckCircleFilled style={{ fontSize: 44, color: token.colorSuccess }} />
              ) : (
                <Spin size="large" />
              )}
              <Typography.Title
                level={2}
                style={{ fontFamily: fontFamilySerif, marginBottom: 0, marginTop: token.marginXS }}
              >
                {liberado ? 'Tudo certo com o seu pagamento' : 'Confirmando o seu pagamento'}
              </Typography.Title>
              <Typography.Text type="secondary">
                {liberado
                  ? compra
                    ? `O ${compra.produto.name} já está na sua conta.`
                    : 'O seu acesso já está liberado.'
                  : desistiu
                    ? 'O provedor ainda não confirmou. Isso costuma levar segundos, mas pode demorar alguns minutos - você não precisa pagar de novo, e o acesso aparece sozinho.'
                    : 'Deve levar poucos segundos. Não feche esta página.'}
              </Typography.Text>
            </Flex>

            {liberado ? <OQueAbriu chaves={chaves} rotulos={doCatalogo?.rotulos} acessos={ativos} /> : null}

            {compra ? <Recibo compra={compra} email={sessao?.user.email} /> : null}

            {liberado ? (
              <Flex vertical gap={token.marginSM}>
                {/*
                  A porta principal é a do que foi comprado. Quem compra uma
                  Legislação Comentada avulsa - produto que o catálogo já
                  admite - cairia no Enac que não comprou, com o botão certo
                  em segundo plano.
                */}
                <Link to={soLeis ? '/legislacao' : '/estudar?module=enac'}>
                  <Button type="primary" size="large" block icon={<RightOutlined />} iconPosition="end">
                    {soLeis ? 'Abrir a Legislação Comentada' : 'Começar a estudar'}
                  </Button>
                </Link>
                <Link to={soLeis ? '/' : '/legislacao'}>
                  <Button size="large" block>
                    {soLeis ? 'Ir para o início' : 'Abrir a Legislação Comentada'}
                  </Button>
                </Link>
              </Flex>
            ) : (
              <Flex vertical gap={token.marginSM}>
                <Link to="/configuracoes/plano">
                  <Button size="large" block icon={<ClockCircleOutlined />}>
                    Ver a situação da minha conta
                  </Button>
                </Link>
              </Flex>
            )}

            <Typography.Text
              type="secondary"
              style={{ textAlign: 'center', fontSize: 12, lineHeight: 1.6 }}
            >
              A compra vale por doze meses e <strong>não é assinatura</strong>: não renova sozinha e
              não há o que cancelar. Nos sete primeiros dias você pode desistir e receber tudo de
              volta.
            </Typography.Text>
          </>
        )}
      </Flex>
    </Flex>
  );
}

/** O que a compra abriu, com a capa de cada produto e o prazo de cada um. */
function OQueAbriu({
  chaves,
  rotulos,
  acessos,
}: {
  chaves: string[];
  rotulos?: Record<string, string>;
  acessos: { product: string; rotulo: string; expiresAt: string | null }[];
}) {
  const { token } = theme.useToken();
  if (chaves.length === 0) return null;

  return (
    <Card size="small" title="O que a sua compra abriu">
      <Flex vertical>
        {chaves.map((chave, indice) => {
          const acesso = acessos.find((item) => item.product === chave);
          const nome = rotulos?.[chave] ?? acesso?.rotulo ?? chave;
          const arte = produtoPorChave(chave, nome);

          return (
            <div key={chave}>
              {indice > 0 ? <Divider style={{ marginBlock: token.marginXS }} /> : null}
              <Flex align="center" gap={token.margin}>
                {arte ? <MiniaturaDoProduto produto={arte} largura={72} /> : null}
                <Flex vertical>
                  <Typography.Text strong>{nome}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {acesso?.expiresAt
                      ? `Vale até ${dataPorExtenso(acesso.expiresAt)}`
                      : 'Acesso sem prazo'}
                  </Typography.Text>
                </Flex>
              </Flex>
            </div>
          );
        })}
      </Flex>
    </Card>
  );
}

/**
 * O recibo.
 *
 * O número do pedido em destaque porque é o que o suporte pede, e porque é a
 * única coisa desta tela que a pessoa vai precisar copiar.
 */
function Recibo({
  compra,
  email,
}: {
  compra: {
    id: string;
    amountCents: number;
    forma: string;
    maxInstallments: number;
    paidAt: string | null;
    createdAt: string;
  };
  email?: string;
}) {
  const { token } = theme.useToken();
  const parcelado = compra.maxInstallments > 1;
  const quando = compra.paidAt ?? compra.createdAt;

  return (
    <Card size="small" title="Recibo">
      <Flex vertical gap={token.marginXS}>
        <Linha rotulo="Valor" valor={reais(compra.amountCents)} />
        <Linha
          rotulo="Forma"
          valor={
            compra.forma === 'pix'
              ? 'Pix, à vista'
              : parcelado
                ? `Cartão, em até ${compra.maxInstallments}x`
                : 'Cartão, à vista'
          }
        />
        <Linha rotulo="Data" valor={dataPorExtenso(quando)} />
        <Linha rotulo="Pedido" valor={<Typography.Text copyable>{compra.id}</Typography.Text>} />
      </Flex>

      {email ? (
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
          Enviamos a confirmação para {email}.
        </Typography.Text>
      ) : null}
    </Card>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: ReactNode }) {
  return (
    <Flex justify="space-between" align="baseline" gap={16} wrap>
      <Typography.Text type="secondary">{rotulo}</Typography.Text>
      <Typography.Text strong style={{ textAlign: 'right' }}>
        {valor}
      </Typography.Text>
    </Flex>
  );
}
